import find from 'lodash/find';

// API
import api from '../../utils/api';

// Actions
import { setActiveId } from './UIActions';

// Models
import Address from '../../models/Address';
import Comparables from '../../models/Comparables';

// Queries
import type { QueryDataObjectType } from '../../utils/queries';
import { getCompsOnlyQuery, getFullQuery } from '../../utils/queries';

// Misc / utils
import { augmentError } from '../../utils/errors';
import constants from '../constants';
import errorConstants from '../../constants/ErrorConstants';

// Types
import type { ByAddressApiType, ComparablesApiType, ListingApiErrorType } from '../../models/ApiResponseTypes';
import type { AppThunkAddress, AppThunkPromise } from '../types';
import { emitErrorEvent } from '../../utils/analytics/ClickstreamEvents';
import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';

// ActionCreators... <verb><Noun>
export const getComparables = (params: QueryDataObjectType): AppThunkPromise => {
  return async (dispatch, getState) => {
    const user = getState().user;
    // Call partial query from API that gets comparables
    const requestBody = getCompsOnlyQuery(params);
    const { data } = await fetchGraphQlData(requestBody, user);
    const userComps = dispatch(setComparables(data as ByAddressApiType));

    return { userComps };
  };
};

export const getAddressAndComparables = (params: QueryDataObjectType): AppThunkPromise => {
  return async (dispatch, getState) => {
    const user = getState().user;

    const requestBody = getFullQuery(params);
    const { data, errors } = await fetchGraphQlData(requestBody, user);

    const userAddress = dispatch(setAddress(data as ByAddressApiType));

    const hasZestimate =
      Array.isArray(errors) &&
      !find(errors, (error) => typeof error === 'object' && error?.code === errorConstants.RENT_ZESTIMATE_NOT_FOUND);
    const hasCompsPrices =
      Array.isArray(errors) &&
      !find(errors, (error) => typeof error === 'object' && error?.code === errorConstants.GET_COMPS_PRICE_EMPTY);

    if (!hasCompsPrices && !hasZestimate) {
      throwNonCriticalError({
        message: "We don't have enough data for this address",
        code: errorConstants.RENT_ZESTIMATE_NOT_FOUND,
      });
    }

    if (hasCompsPrices) {
      await dispatch(setComparables(data as ByAddressApiType));
    }

    return {
      userAddress,
    };
  };
};

// API fetches
export const fetchGraphQlData = async (requestBody: string, user: ZrmClickstreamUser) => {
  try {
    const { data, errors } = await api.address.graphQlListing(requestBody);

    // Throw on major errors, otherwise handle errors in places where they cause issues
    if (Array.isArray(errors) && errors.length > 0) {
      if (containsError(errors, errorConstants.PROPERTY_DETAILS_NOT_FOUND)) {
        // If listing can't be found by our API, throw error and STOP
        emitErrorEvent({ user });
        throwNonCriticalError({
          message: 'We were unable to match this address',
          code: errorConstants.PROPERTY_DETAILS_NOT_FOUND,
        });
      }

      if (containsError(errors, errorConstants.GET_COMPS_PRICE_EMPTY)) {
        emitErrorEvent({ user });
      }

      if (containsError(errors, errorConstants.RENT_ZESTIMATE_NOT_FOUND)) {
        emitErrorEvent({ user });
      }
    }

    return { data, errors };
  } catch (error) {
    augmentError(error, {
      action: 'Error in AddressActions#fetchGraphQlData',
    });

    throw error;
  }
};

// Setters
export const setAddress = (data: ByAddressApiType): AppThunkAddress => {
  return (dispatch) => {
    const addressData = {
      ...data.byAddress.floorplans[0],
      geo: data.byAddress.geo,
      marketSummary: data.byAddress.marketSummary,
    };
    // Set the user's address
    const modeledAddressData = new Address(addressData);

    dispatch({
      type: constants.SET_ADDRESS,
      payload: modeledAddressData,
    });

    modeledAddressData.listingId && dispatch(setActiveId(modeledAddressData.listingId));

    return modeledAddressData;
  };
};

export const setComparables = (data: ByAddressApiType) => {
  return (dispatch, getState) => {
    const {
      floorplans,
      listingRanking,
      lowestPriceIncluded: min,
      highestPriceIncluded: max,
    } = data.byAddress.similarFloorplans;

    // Set the user's comparables
    const compsItems = floorplans.map((comp: ComparablesApiType) => {
      return new Comparables(comp);
    });

    const userProperty = getState().address;
    compsItems.splice(listingRanking, 0, userProperty);
    const userPropertyIdx = compsItems.findIndex((comp) => comp.isUsersComp);

    dispatch({
      type: constants.SET_COMPARABLES,
      payload: {
        min,
        max,
        items: compsItems,
        userPropertyIdx,
      },
    });

    // set activeId to userProperty ID after comps load to scroll to home
    dispatch(setActiveId(userProperty.listingId));

    return compsItems;
  };
};

// Errors
const throwNonCriticalError = ({ message, code }: { message: string; code: string }) => {
  throw {
    nonCriticalError: true,
    errorCode: code,
    errorMsg: message,
  };
};

const containsError = (errors: Array<ListingApiErrorType>, errorConstant: string) => {
  return find(errors, (error) => error.code === errorConstant);
};
